import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  useListMyFormsQuery, useSubmitFormMutation,
  useGetFormDraftQuery, useSaveFormDraftMutation, useDeleteFormDraftMutation,
} from '../../app/api';
import RoleAlignmentForm from './RoleAlignmentForm';

const brand = {
  navy: '#002040', orange: '#F09010', blue: '#0060A0', softGray: '#e9e6e1', charcoal: '#58585b',
};

const FORM_KEY = 'role_alignment';

// Per-user draft key so entries survive navigating away (and the form is
// scoped to the signed-in person on a shared browser).
const draftKey = () => `${FORM_KEY}_draft:${window.localStorage.getItem('e') || ''}`;

// How long after the last keystroke to write the draft to the server. Long
// enough that typing a paragraph is one save rather than forty; short enough that
// closing the laptop mid-thought loses nothing anyone would notice. localStorage
// is already written on every change, so this delay is never the difference
// between keeping and losing work on the same machine.
const SAVE_DEBOUNCE_MS = 2500;

function savedAgo(iso) {
  if (!iso) return '';
  const secs = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 1000));
  if (secs < 45) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString();
}

// Employee-facing Role Alignment Questionnaire. Visible only to employees HR has
// granted the form to (company_directory.enabled_forms) — the page re-checks
// access via /forms/my_forms so a direct URL hit by an ungranted user is bounced
// to a friendly notice rather than a 403 on submit.
//
// The form is long and reflective by design, so it is meant to be filled in over
// more than one sitting. Answers are saved twice over: to localStorage on every
// change, and to the server on a debounce, which is what lets someone resume on a
// different day or a different machine. Nothing is visible to HR, to a manager, or
// to anyone else until Submit — drafts live in their own table with no read path
// for anybody but their author.
export default function RoleAlignment() {
  const { data, isLoading } = useListMyFormsQuery();
  const [submitForm, { isLoading: submitting }] = useSubmitFormMutation();
  const [saveDraft] = useSaveFormDraftMutation();
  const [deleteDraft] = useDeleteFormDraftMutation();
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const entry = (data?.forms || []).find((f) => f.key === FORM_KEY);
  const hasAccess = Boolean(entry);
  // my_forms already tracks this — the page just never read it, so a return visit
  // after submitting showed a blank form (and allowed a second submission).
  const alreadySubmitted = Boolean(entry?.submitted);

  // Don't ask for a draft we'd never use: someone who has already submitted sees
  // the thank-you panel, and someone without access sees the notice.
  const wantsDraft = hasAccess && !alreadySubmitted;
  const { data: draftData, isLoading: draftLoading } = useGetFormDraftQuery(
    { form_key: FORM_KEY }, { skip: !wantsDraft },
  );

  // 'idle' | 'saving' | 'saved' | 'local' — 'local' means the server write failed
  // and the only copy is in this browser, which the user needs to be told
  // plainly rather than being shown a reassuring "saved".
  const [saveState, setSaveState] = useState('idle');
  const [savedAt, setSavedAt] = useState(null);
  const timer = useRef(null);
  const latest = useRef(null);

  const pushDraft = useCallback(async (responses) => {
    setSaveState('saving');
    try {
      await saveDraft({ form_key: FORM_KEY, responses }).unwrap();
      setSavedAt(new Date().toISOString());
      setSaveState('saved');
    } catch {
      // localStorage already holds this, so nothing is lost on this machine —
      // but it will not be on the next one, and saying "saved" would be a lie.
      setSaveState('local');
    }
  }, [saveDraft]);

  const onResponsesChange = useCallback((responses) => {
    latest.current = responses;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => pushDraft(responses), SAVE_DEBOUNCE_MS);
  }, [pushDraft]);

  // A pending debounce must not outlive the page: leaving with an unsaved edit is
  // exactly the case this whole feature exists for.
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const saveNow = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    if (latest.current) pushDraft(latest.current);
  }, [pushDraft]);

  const onSubmit = async (responses) => {
    setError(null);
    // Cancel any in-flight autosave: the answers are about to become a
    // submission, and a debounced write landing afterwards would recreate the
    // draft we are about to delete.
    if (timer.current) clearTimeout(timer.current);
    try {
      await submitForm({ form_key: FORM_KEY, responses }).unwrap();
      // Submitted successfully — clear the saved draft so it doesn't linger.
      // The server clears its own copy as part of the submit; this is the local
      // one.
      try { window.localStorage.removeItem(draftKey()); } catch { /* non-fatal */ }
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setError(e?.data?.detail || 'Could not submit the form. Please try again.');
    }
  };

  const onDiscardDraft = async () => {
    if (!window.confirm('Discard your saved answers and start this form over? This cannot be undone.')) return;
    if (timer.current) clearTimeout(timer.current);
    try { window.localStorage.removeItem(draftKey()); } catch { /* non-fatal */ }
    try { await deleteDraft({ form_key: FORM_KEY }).unwrap(); } catch { /* the reload is what matters */ }
    // Remount from a clean slate rather than trying to reset every field in place.
    window.location.reload();
  };

  const draftStatus = (() => {
    if (saveState === 'saving') return 'Saving…';
    if (saveState === 'local') return 'Saved on this device only — we could not reach the server, so finish on this browser or try again.';
    if (saveState === 'saved') return `Saved ${savedAgo(savedAt)}. You can close this and come back later.`;
    if (draftData?.draft?.updated) {
      return `Continuing your saved answers from ${savedAgo(draftData.draft.updated)}.`;
    }
    return 'Your answers save automatically as you type.';
  })();

  const showForm = hasAccess && !alreadySubmitted && !done;

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: '1.5rem' }}>
      <div style={{ maxWidth: 860, margin: '0 auto 1.25rem' }}>
        <h4 style={{ color: brand.navy, fontWeight: 800, margin: 0 }}>Role Alignment Questionnaire</h4>
        <p style={{ color: brand.charcoal, margin: '0.25rem 0 0', fontSize: '0.9rem' }}>
          Share which responsibilities energize you and where you see yourself growing. There are no wrong answers.
          You don’t have to finish in one sitting — your answers are saved as you go and stay private until you submit.
        </p>
      </div>

      {isLoading || (showForm && draftLoading) ? (
        // Waiting for the draft before mounting the form, not after: the form
        // seeds its state once, so rendering it first and then delivering the
        // draft would show a blank questionnaire and drop the saved answers.
        <div className="text-center py-5"><div className="spinner-border text-primary" role="status" /></div>
      ) : !hasAccess ? (
        <div style={{ maxWidth: 860, margin: '0 auto', background: 'white', borderRadius: 12, padding: '2rem', textAlign: 'center', color: brand.charcoal }}>
          This form isn’t currently assigned to you. If you believe you should have access, please contact HR.
        </div>
      ) : done || alreadySubmitted ? (
        <div style={{ maxWidth: 860, margin: '0 auto', background: 'white', borderRadius: 12, padding: '2rem', textAlign: 'center' }}>
          <i className="bi bi-check-circle-fill" style={{ color: '#2E7D32', fontSize: '2rem' }} />
          <h5 style={{ color: brand.navy, fontWeight: 800, margin: '0.75rem 0 0.25rem' }}>Thank you!</h5>
          <p style={{ color: brand.charcoal, margin: 0, fontSize: '0.9rem' }}>
            Your responses have been submitted. Your Advisor Success team member will follow up to
            schedule a Role Alignment Conversation.
          </p>
        </div>
      ) : (
        <>
          {error && (
            <div style={{ maxWidth: 860, margin: '0 auto 1rem', color: '#B71C1C', fontSize: '0.88rem' }}>
              <i className="bi bi-exclamation-circle me-1" /> {error}
            </div>
          )}
          <RoleAlignmentForm
            mode="fill"
            onSubmit={onSubmit}
            submitting={submitting}
            // The server draft is the base; the form layers a NEWER local draft
            // over it and leaves an older one alone.
            initial={{ full_name: data?.name || '', ...(draftData?.draft?.responses || {}) }}
            initialSavedAt={draftData?.draft?.updated || null}
            storageKey={draftKey()}
            onResponsesChange={onResponsesChange}
            draftStatus={draftStatus}
            onSaveLater={saveNow}
            onDiscardDraft={draftData?.draft ? onDiscardDraft : null}
          />
        </>
      )}
    </div>
  );
}
